// The cell cursor of an editable grid (design-system.md §4.9), as pure functions: the grid owns the keys and the
// focus, these say where the cursor goes and which key starts an edit.

export type CellInputMode = "decimal" | "text";

interface CellPosition {
  row: number;
  col: number;
}

/** The column `delta` steps away, without leaving the grid. */
export function stepColumn(col: number, delta: number, count: number): number {
  return Math.max(0, Math.min(col + delta, count - 1));
}

/**
 * The next cell that can be edited after `from` (Tab), or before it (Shift + Tab): along the row, then on the rows
 * that follow. Null at the end of the grid: the focus goes back to it.
 */
export function nextEditable(
  from: CellPosition,
  direction: 1 | -1,
  grid: { rows: number; editableCols: readonly number[]; canEdit: (row: number, col: number) => boolean },
): CellPosition | null {
  const cols = direction === 1 ? grid.editableCols : [...grid.editableCols].reverse();
  for (let row = from.row; row >= 0 && row < grid.rows; row += direction) {
    for (const col of cols) {
      const ahead = row !== from.row || (direction === 1 ? col > from.col : col < from.col);
      if (ahead && grid.canEdit(row, col)) return { row, col };
    }
  }
  return null;
}

/**
 * Whether typing `key` on a cell starts editing it with that character. Only in a number cell, and only what a
 * number is made of: letters stay the shortcuts of the grid (j, k, x) and of the app (g, /).
 */
export function startsEdit(key: string, mode: CellInputMode): boolean {
  return mode === "decimal" && /^[\d.,]$/.test(key);
}
